import React, { forwardRef } from 'react'
import { Card, Stack, Text, Flex, Box, Badge, Label, Grid } from '@sanity/ui'
import { ObjectInputProps } from 'sanity'

interface PerformanceScores {
  wheelWidthScore?: number
  tireWidthScore?: number
  tireCategoryScore?: number
  diameterScore?: number
  rawScore?: number
  normalizedScore?: number
  lastCalculated?: string
  contextHash?: string
}

const PerformanceScoreDisplay = forwardRef<HTMLDivElement, ObjectInputProps>(
  (props, ref) => {
    const { value, schemaType } = props
    const scores = value as PerformanceScores | undefined

    const formatDate = (dateString?: string) => {
      if (!dateString) return 'Never'
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    }

    const formatScore = (score?: number) => {
      if (score === undefined || score === null) return '-'
      return score.toFixed(2)
    }

    const hasScores = scores?.normalizedScore !== undefined

    if (!hasScores) {
      return (
        <Stack gap={3} ref={ref}>
          <Box>
            <Label size={1}>{schemaType.title}</Label>
          </Box>
          <Card tone="transparent" padding={4} border radius={2}>
            <Stack gap={3} style={{ textAlign: 'center' }}>
              <Text size={2}>No Performance Score</Text>
              <Text size={1} muted>
                Use the "Calculate Performance Scores" button on the buyer guide to generate scores.
              </Text>
            </Stack>
          </Card>
        </Stack>
      )
    }

    return (
      <Stack gap={4} ref={ref}>
        <Box>
          <Label size={1}>{schemaType.title}</Label>
        </Box>

        <Card padding={4} radius={2} tone="positive" border>
          <Flex align="center" justify="space-between">
            <Stack gap={2}>
              <Text size={1} muted>
                Normalized Score
              </Text>
              <Text size={4} weight="bold">
                {formatScore(scores.normalizedScore)} / 10
              </Text>
            </Stack>
            <Badge tone="positive" fontSize={1} padding={2}>
              Raw: {formatScore(scores.rawScore)}
            </Badge>
          </Flex>
        </Card>

        <Card padding={3} radius={2} border>
          <Stack gap={3}>
            <Text size={1} weight="semibold">
              Sub-Scores Breakdown
            </Text>
            <Grid gridTemplateColumns={2} gap={3}>
              <Card padding={2} radius={1} tone="default">
                <Stack gap={1}>
                  <Text size={0} muted>Wheel Width</Text>
                  <Text size={2} weight="semibold">{formatScore(scores.wheelWidthScore)}</Text>
                </Stack>
              </Card>
              <Card padding={2} radius={1} tone="default">
                <Stack gap={1}>
                  <Text size={0} muted>Tire Width</Text>
                  <Text size={2} weight="semibold">{formatScore(scores.tireWidthScore)}</Text>
                </Stack>
              </Card>
              <Card padding={2} radius={1} tone="default">
                <Stack gap={1}>
                  <Text size={0} muted>Tire Category</Text>
                  <Text size={2} weight="semibold">{formatScore(scores.tireCategoryScore)}</Text>
                </Stack>
              </Card>
              <Card padding={2} radius={1} tone="default">
                <Stack gap={1}>
                  <Text size={0} muted>Diameter</Text>
                  <Text size={2} weight="semibold">{formatScore(scores.diameterScore)}</Text>
                </Stack>
              </Card>
            </Grid>
          </Stack>
        </Card>

        <Text size={0} muted>
          Last calculated: {formatDate(scores.lastCalculated)}
        </Text>
      </Stack>
    )
  }
)

PerformanceScoreDisplay.displayName = 'PerformanceScoreDisplay'

export default PerformanceScoreDisplay
